import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LosStateService } from './los-state.service';
import { LoanApplicationItem } from '../loan-applications/loan-applications.interface';
import { ToastrService } from 'ngx-toastr';

/** Status-to-step mapping — determines the maximum reachable step for a given applicationStatus. */
const STATUS_STEP_MAP: Record<string, number> = {
  'Draft':             0,
  'ConsentPending':    1,
  'KYCPending':        2,
  'KYCVerified':       3,
  'FinancialComplete': 4,
  'BureauComplete':    4,
  'CreditEvaluated':   5,
  'Approved':          6,
  'Rejected':          6,
  'KFSDelivered':      7,
  'eStamped':          8,
  'eSigned':           9,
  'eMandateComplete':  10,
  'DisbursalReady':    11,
  'Disbursed':         12
};

@Component({
  selector: 'app-loan-application-master',
  templateUrl: './loan-application-master.component.html',
  styleUrls: ['./loan-application-master.component.css'],
  providers: [LosStateService]
})
export class LoanApplicationMasterComponent implements OnInit {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private losState   = inject(LosStateService);
  private toastr     = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationId: number | null = null;
  applicationData: LoanApplicationItem | null = null;
  isLoading = true;
  currentStep = 0;
  private isInitialLoad = true;

  steps: string[] = [
    'App create',
    'Consent',
    'KYC',
    'Financial',
    'Bureau+AA',
    'Decision',
    'BO review',
    'KFS',
    'eStamp',
    'eSign',
    'eMandate',
    'Disbursal ready',
    'Disbursed'
  ];

  ngOnInit(): void {
    // Subscribe to loading state
    this.losState.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(l => this.isLoading = l);

    // Subscribe to application data for step-lock logic
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        if (app && this.isInitialLoad) {
          const hasStep = this.route.snapshot.queryParamMap.has('Step') || this.route.snapshot.queryParamMap.has('step');
          if (!hasStep) {
            this.currentStep = this.maxReachableStep;
            this.syncUrl();
          }
          this.isInitialLoad = false;
        }
      });

    // Read applicationId from query params
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const id = params['ApplicationId'] || params['applicationId'];
        const step = params['Step'];
        if (id) {
          const newId = +id;
          if (newId !== this.applicationId) {
            this.applicationId = newId;
            this.isInitialLoad = true;
            this.losState.initialize(this.applicationId);
          }
          if (step != null) {
            this.currentStep = +step;
            this.isInitialLoad = false;
          }
        }
      });
  }

  /** Maximum step the user is allowed to reach based on current applicationStatus. */
  get maxReachableStep(): number {
    if (!this.applicationData) return 0;
    const status = this.applicationData.applicationStatus || 'Draft';
    return STATUS_STEP_MAP[status] ?? 0;
  }

  /** Whether a given step tab is navigable. */
  canNavigateToStep(index: number): boolean {
    // Allow going back to any completed step, and forward up to maxReachableStep + 1
    return index <= this.maxReachableStep + 1;
  }

  goToStep(index: number): void {
    if (!this.canNavigateToStep(index)) {
      this.toastr.warning('Complete the current step before proceeding.');
      return;
    }
    this.currentStep = index;
    this.syncUrl();
  }

  nextStep(): void {
    if (this.currentStep < this.steps.length - 1) {
      // Refresh application state so step-lock re-evaluates
      if (this.applicationId) {
        this.losState.refreshApplication(this.applicationId);
      }
      this.currentStep++;
      this.syncUrl();
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.syncUrl();
    }
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ApplicationId: this.applicationId,
        Step: this.currentStep
      },
      queryParamsHandling: 'merge'
    });
  }
}
